import React, { useEffect, useState, useRef, useCallback } from "react";
import HTMLFlipBook from "react-pageflip";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

// ─── PDFのオリジナルアスペクト比（幅:高さ）を取得 ──────────────
const getPageAspectRatio = async (pdf) => {
  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale: 1.0 });
  return viewport.width / viewport.height;
};

// ─── 1ページをBlobURLに変換 ─────────────────────────────────────
const renderPageToBlobUrl = async (page) => {
  const viewport = page.getViewport({ scale: 2.0 });
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({ canvasContext: context, viewport }).promise;

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(URL.createObjectURL(blob));
    }, "image/jpeg", 0.92);
  });
};

const PdfFlipBook = ({ pdfUrl }) => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookSize, setBookSize] = useState(null); // null = 未計算
  const wrapperRef = useRef(null);
  const aspectRatioRef = useRef(1 / Math.sqrt(2));
  const sizeLockedRef = useRef(false); // 一度確定したらロック

  // ─── サイズ計算（ロック後は呼んでも何もしない）──────────────
  const calcBookSize = useCallback(() => {
    if (sizeLockedRef.current) return;
    const el = wrapperRef.current;
    if (!el) return;

    const wrapperW = el.clientWidth;
    if (wrapperW < 10) return; // まだレイアウト未確定

    // モーダルの実高さ（header・footer除く）を取得
    let availableH = 0;
    const modalContent = document.querySelector(".pdf-modal-dialog .modal-content");
    if (modalContent) {
      const header = modalContent.querySelector(".modal-header");
      const footer = modalContent.querySelector(".modal-footer");
      const headerH = header ? header.offsetHeight : 0;
      const footerH = footer ? footer.offsetHeight : 0;
      availableH = modalContent.offsetHeight - headerH - footerH;
    }
    if (availableH < 100) {
      // フォールバック：95vh - header(56px) - footer(65px)
      availableH = window.innerHeight * 0.95 - 121;
    }

    const ratio = aspectRatioRef.current;

    // 高さ基準
    const pageWFromH = Math.floor(availableH * ratio);
    // 幅基準（見開き2ページ）
    const pageWFromW = Math.floor(wrapperW / 2);
    const pageHFromW = Math.floor(pageWFromW / ratio);

    let pageW, pageH;
    if (pageWFromH * 2 <= wrapperW) {
      pageW = pageWFromH;
      pageH = availableH;
    } else {
      pageW = pageWFromW;
      pageH = pageHFromW;
    }

    if (pageW < 100 || pageH < 100) return;

    // ✅ サイズ確定 → 以降の再計算をロック
    sizeLockedRef.current = true;
    setBookSize({ width: pageW, height: pageH });
  }, []);

  // ─── マウント時・wrapperリサイズ時のみ計算（windowリサイズは別途）
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    // wrapperのみ監視（modal-contentは監視しない＝ループ防止）
    const ro = new ResizeObserver(() => calcBookSize());
    ro.observe(el);
    calcBookSize();

    const onResize = () => {
      // ウィンドウリサイズ時はロックを解除して再計算
      sizeLockedRef.current = false;
      calcBookSize();
    };
    window.addEventListener("resize", onResize);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, [calcBookSize]);

  // ─── PDF読み込み ─────────────────────────────────────────────
  useEffect(() => {
    if (!pdfUrl) return;

    let cancelled = false;
    const blobUrls = [];

    const loadPdf = async () => {
      setPages([]);
      setLoading(true);
      setError(null);

      try {
        const pdf = await pdfjsLib.getDocument(pdfUrl).promise;

        aspectRatioRef.current = await getPageAspectRatio(pdf);
        // アスペクト比取得後にサイズ再計算（ロック前なので有効）
        sizeLockedRef.current = false;
        calcBookSize();

        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) break;
          const page = await pdf.getPage(i);
          const blobUrl = await renderPageToBlobUrl(page);
          blobUrls.push(blobUrl);
          if (!cancelled) setPages((prev) => [...prev, blobUrl]);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("PDF読み込みエラー:", err);
          setError("PDFの読み込みに失敗しました。");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
      blobUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [pdfUrl, calcBookSize]);

  if (error) {
    return (
      <div style={{ textAlign: "center", padding: "2rem", color: "#c00" }}>
        {error}
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      style={{
        width: "100%",
        overflow: "hidden",
        boxSizing: "border-box",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {loading && pages.length === 0 ? (
        <div style={{ textAlign: "center", padding: "2rem" }}>
          PDF読み込み中...
        </div>
      ) : bookSize === null ? (
        // サイズ未確定中は何も描画しない（チラつき防止）
        <div style={{ textAlign: "center", padding: "2rem" }}>
          表示準備中...
        </div>
      ) : (
        <HTMLFlipBook
          key={`${bookSize.width}-${bookSize.height}`}
          width={bookSize.width}
          height={bookSize.height}
          size="fixed"
          minWidth={bookSize.width}
          maxWidth={bookSize.width}
          minHeight={bookSize.height}
          maxHeight={bookSize.height}
          maxShadowOpacity={0.5}
          showCover={true}
          mobileScrollSupport={true}
        >
          {pages.map((img, i) => (
            <div key={`page-${i}`} style={{ width: "100%", height: "100%" }}>
              <img
                src={img}
                alt={`${i + 1}ページ目`}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </div>
          ))}
        </HTMLFlipBook>
      )}
    </div>
  );
};

export default PdfFlipBook;
